import type { AnnotationLabelT } from '../common/types/types';
import type { OutputRelationshipT } from '../modernization-common/relationships';
import type { QAFailingCheck } from '../qa-checks/type/output-metadata';
import type { MetadataPayload } from './labelling-v2/studio-store/types';
export type LabelValidationT = {
  isValid: boolean;
  reasons: Array<string>;
};
export const MaskOutputFormat = ({
  MASK_OUTPUT_FORMAT_BASE64: 'MASK_OUTPUT_FORMAT_BASE64',
  MASK_OUTPUT_FORMAT_RLE: 'MASK_OUTPUT_FORMAT_RLE'
} as const);
export type MaskOutputFormat = (typeof MaskOutputFormat)[keyof typeof MaskOutputFormat];
export const SegmentImageModelName = ({
  SEGMENT_IMAGE_MODEL_NAME_SAM2: 'SEGMENT_IMAGE_MODEL_NAME_SAM2',
  SEGMENT_IMAGE_MODEL_NAME_SAM3: 'SEGMENT_IMAGE_MODEL_NAME_SAM3'
} as const);
export type SegmentImageModelName = (typeof SegmentImageModelName)[keyof typeof SegmentImageModelName];
export type SamModelImagePluginRequestT = {
  imageUrl: string;
  imageSegmentationType: ImageSegmentationType;
  points?: SegmentImagePoint[];
  box?: SegmentImageBox;
  confidenceThreshold?: number;
  modelName?: SegmentImageModelName;
  maskOutputFormat?: MaskOutputFormat;
};
export type SamModelVideoPluginRequestT = {
  videoUrl: string;
  videoSegmentationType: VideoSegmentationType;
  points?: SegmentPoint[];
  box?: SegmentBox;
  frameIndex: number;
  frameRange: {
    startFrame: number;
    endFrame: number;
  };
};
export enum VideoSegmentationType {
  VIDEO_SEGMENTATION_TYPE_INVALID = 'VIDEO_SEGMENTATION_TYPE_INVALID',
  VIDEO_SEGMENTATION_TYPE_MASK = 'VIDEO_SEGMENTATION_TYPE_MASK',
  VIDEO_SEGMENTATION_TYPE_POLYGON = 'VIDEO_SEGMENTATION_TYPE_POLYGON',
}
export type SegmentPoint = {
  x: number;
  y: number;
  includeInMask: boolean;
};
export type SegmentBox = {
  xStart: number;
  xEnd: number;
  yStart: number;
  yEnd: number;
};
export type SamModelPluginRequestT = SamModelImagePluginRequestT | SamModelVideoPluginRequestT;
export type BoundingBoxTrackingInputT = {
  videoUrl: string;
  initialFrame: number;
  bbox: {
    xStart: number;
    yStart: number;
    xEnd: number;
    yEnd: number;
  };
  numFramesToTrack?: number;
};
export type BoundingBoxTrackingOutputT = {
  bboxList: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>;
};

// Batch keyframe tracking (/video/sam-2/track-objects). One request carries all of a
// single object's adjusted keyframes; the BE resolves the media from `taskId` (no videoUrl)
// and returns one tracked object spanning [startFrame, endFrame]. Frame indices are 0-based.
export type TrackObjectsInputT = {
  taskId: number;
  keyFrames: Array<{
    frameNo: number;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }>;
  startFrame: number;
  endFrame: number;
};

// The client injected into the shared studio (via APIContext) is already bound to a task.
export type TrackObjectsRequestT = Omit<TrackObjectsInputT, 'taskId'>;
export type TrackedFrameT = {
  frame: number;
  bbox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  confidence: number;
  status: string;
  anchor: boolean;
};
export type TrackedObjectT = {
  objectId: string;
  frames: TrackedFrameT[];
};
export type TrackObjectsOutputT = {
  success: boolean;
  error: string | null;
  data: {
    framesProcessed: number;
    objectsProcessed: number;
    status: string;
    partial: boolean;
    error: string | null;
    objects: TrackedObjectT[];
  };
};
export type SegmentImageBox = {
  xStart: number;
  xEnd: number;
  yStart: number;
  yEnd: number;
};
export type SamModelImagePluginResponseT = {
  predictions: Prediction[];
};
export type SamModelVideoPluginResponseT = {
  videoPredictions: VideoPrediction[];
};
export type VideoPrediction = {
  prediction: Prediction;
  frameNumber: number;
};
export type SamModelPluginResponseT = SamModelImagePluginResponseT | SamModelVideoPluginResponseT;
export type Prediction = {
  mask?: string;
  score: number;
  polygon?: Points;
};
export type Point = {
  x: number;
  y: number;
};
export type Points = {
  points: Point[];
};
export type SegmentImagePoint = Point & {
  includeInMask: boolean;
};
export enum ImageSegmentationType {
  IMAGE_SEGMENTATION_TYPE_INVALID = 'IMAGE_SEGMENTATION_TYPE_INVALID',
  IMAGE_SEGMENTATION_TYPE_MASK = 'IMAGE_SEGMENTATION_TYPE_MASK',
  IMAGE_SEGMENTATION_TYPE_POLYGON = 'IMAGE_SEGMENTATION_TYPE_POLYGON',
}
type RequireAtLeastOne<T> = { [K in keyof T]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<keyof T, K>>> }[keyof T];
export type SAMSegmentImageData = RequireAtLeastOne<{
  points: [number, number][];
  mask: string;
}> & {
  automationRequestId?: string;
  rawPointCount?: number;
  simplifiedPointCount?: number;
  epsilonUsed?: number;
  rawContour?: [number, number][];
};
export type SAMSegmentVideoData = SAMSegmentImageData[];
export type Sam3TextPromptRequestT = {
  imageUrl: string;
  textPrompt: string;
  modelName: string;
  maskOutputFormat?: MaskOutputFormat;
};
export enum TimeDisplayMode {
  FRAMES = 'FRAMES',
  TIMESTAMP = 'TIMESTAMP',
}
export type WindowedStudioSubmitData = {
  annotationMetadata: Map<string, MetadataPayload>;
  annotationLabels: AnnotationLabelT[];
  relationships: Record<string, OutputRelationshipT>;
};
export type StudioValidations = {
  containsPendingItem: boolean;
  containsRejectedAnnotation: boolean;
};
export type StudioRef = {
  selectAnnotation: (frameIndex: number, annotationId: string) => void;
  drainJournal: () => Promise<void>;
  getPendingJournalCount: () => Promise<number>;
  getWindowedSubmitData: () => WindowedStudioSubmitData | null;
  getValidations: () => StudioValidations;
};
export type ImageQAFailingCheck = QAFailingCheck & {
  frame: {
    start: number;
    end: number;
  };
};
export type ImageOnChangeMetadata = {
  type: 'image';
  failingChecks: ImageQAFailingCheck[];
};